import { getImprintCopy } from '../i18n.js';
import agbText from '../../../Impressum/AGB.txt';
import privacyText from '../../../Impressum/Datenschutz.txt';

const html = String.raw;

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderLines = (lines = []) => lines.map((line) => escapeHtml(line)).join('<br />');

const normalizeText = (value = '') => String(value).replace(/\r\n/g, '\n').trim();

const renderTextBlocks = (text = '') => {
  const blocks = normalizeText(text)
    .split(/\n\s*\n/g)
    .map((block) => block.trim())
    .filter(Boolean);
  return blocks
    .map((block) => {
      const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
      const isList = lines.length > 1 && lines.every((line) => line.startsWith('-'));
      if (isList) {
        const items = lines
          .map((line) => line.replace(/^-+\s*/, ''))
          .map((line) => `<li>${escapeHtml(line)}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }
      const paragraph = escapeHtml(lines.join('\n')).replace(/\n/g, '<br />');
      return `<p>${paragraph}</p>`;
    })
    .join('');
};

export const renderImprintView = (container) => {
  const copy = getImprintCopy();
  const provider = copy.provider ?? {};
  const contact = copy.contact ?? {};
  const vat = copy.vat ?? {};
  const supervision = copy.supervision ?? {};
  const profession = copy.profession ?? {};
  const dispute = copy.dispute ?? {};
  const editorial = copy.editorial ?? {};
  const notes = copy.notes ?? {};
  const tabs = copy.tabs ?? { imprint: 'Impressum', agb: 'AGBs', privacy: 'Datenschutz' };
  const state = { activeTab: 'imprint' };

  const hashQuery = window.location.hash.split('?')[1] ?? '';
  if (hashQuery) {
    const params = new URLSearchParams(hashQuery);
    const tabParam = params.get('tab');
    if (tabParam && ['imprint', 'agb', 'privacy'].includes(tabParam)) {
      state.activeTab = tabParam;
    }
  }

  const renderImprintSection = () => html`
    <section class="section-card stack">
      <h2>${provider.heading ?? ''}</h2>
      <p>
        <strong>${provider.nameLabel ?? ''}:</strong><br />
        ${escapeHtml(provider.name ?? '')}
      </p>
      <p>
        <strong>${provider.addressLabel ?? ''}:</strong><br />
        ${renderLines(provider.addressLines ?? [])}
      </p>
      <p>
        <strong>${provider.registerLabel ?? ''}:</strong><br />
        ${escapeHtml(provider.registerNote ?? '')}
      </p>
    </section>

    <section class="section-card stack">
      <h2>${contact.heading ?? ''}</h2>
      ${
        contact.email
          ? html`<p>
              <strong>${contact.emailLabel ?? ''}:</strong>
              <a href="mailto:${escapeHtml(contact.email)}">${escapeHtml(contact.email)}</a>
            </p>`
          : ''
      }
      <p>
        <strong>${contact.phoneLabel ?? ''}:</strong>
        ${escapeHtml(contact.phone ?? '')}
      </p>
      <p>
        <strong>${vat.heading ?? ''}:</strong><br />
        ${escapeHtml(vat.text ?? '')}
      </p>
    </section>

    <section class="section-card stack">
      <h2>${supervision.heading ?? ''}</h2>
      <p>${escapeHtml(supervision.text ?? '')}</p>
      <h3>${profession.heading ?? ''}</h3>
      <p>${escapeHtml(profession.text ?? '')}</p>
      <h3>${editorial.heading ?? ''}</h3>
      <p>${escapeHtml(editorial.text ?? '')}</p>
    </section>

    <section class="section-card stack">
      <h2>${dispute.heading ?? ''}</h2>
      <p>${escapeHtml(dispute.text ?? '')}</p>
      ${
        dispute.platformLabel
          ? html`<p>
              <a href="${escapeHtml(dispute.platformLabel)}" target="_blank" rel="noreferrer">
                ${escapeHtml(dispute.platformLabel)}
              </a>
            </p>`
          : ''
      }
      <p>${escapeHtml(dispute.participation ?? '')}</p>
      ${notes.lastUpdated ? html`<p class="muted tiny">${escapeHtml(notes.lastUpdated)}</p>` : ''}
    </section>
  `;

  const renderLegalSection = (title, text) => html`
    <section class="section-card stack">
      <h2>${title}</h2>
      <div class="legal-text">
        ${renderTextBlocks(text)}
      </div>
    </section>
  `;

  const renderTabContent = () => {
    if (state.activeTab === 'agb') {
      return renderLegalSection(tabs.agb, agbText);
    }
    if (state.activeTab === 'privacy') {
      return renderLegalSection(tabs.privacy, privacyText);
    }
    return renderImprintSection();
  };

  const render = () => {
    container.innerHTML = html`
      <div class="stack" id="imprint-view">
        <section class="section-card stack">
          <h1>${copy.title ?? 'Impressum'}</h1>
          <p class="muted">${copy.intro ?? ''}</p>
        </section>
        <div class="tabs">
          <button class="tab ${state.activeTab === 'imprint' ? 'active' : ''}" data-tab="imprint">
            ${tabs.imprint}
          </button>
          <button class="tab ${state.activeTab === 'agb' ? 'active' : ''}" data-tab="agb">
            ${tabs.agb}
          </button>
          <button class="tab ${state.activeTab === 'privacy' ? 'active' : ''}" data-tab="privacy">
            ${tabs.privacy}
          </button>
        </div>
        <div class="tab-content">
          ${renderTabContent()}
        </div>
      </div>
    `;

    container.querySelectorAll('[data-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        state.activeTab = tab.dataset.tab;
        render();
      });
    });
  };

  render();
};
